import React, { useEffect, useMemo, useCallback, useState } from "react";
import { InputTypeAhead, LabelDollarFormatted } from "@loanbossinc/component-library";
import _ from "lodash";
import CircularProgress from "@material-ui/core/CircularProgress";
import { useFlags } from "launchdarkly-react-client-sdk";
import { InputGroup } from "./inputGroup";
import { dscrTypes, DSCR_TYPE_NAMES, validateDscrInputs } from "./helpers";
import DscrFromFile from "./dscrFromFile";
import {
  InputDropdownWrapper,
  InputGroupLabel,
  ComparisonHeaderContainer,
  ResultPreviewContainer,
  ResultPreviewLabel,
  TypeSelectionContainerComparison,
  StyledOr,
  InputGroupsContainer,
  InputGroupSection,
  VerticalSeparator,
  StyledButtonText,
  PreviewWrapper,
  PreviewButtonWrapper,
  SwitchWrapper,
  StyledLockedSwitch,
  SwitchLabelWrapper
} from "./styles";
const OverrideSwitchComparison = ({ value, onToggleChange, fieldLabel }) => {
  return (
    <SwitchLabelWrapper>
      <InputGroupLabel>{fieldLabel}</InputGroupLabel>
      <SwitchWrapper>
        <StyledLockedSwitch value={value} onChange={onToggleChange} />
      </SwitchWrapper>
    </SwitchLabelWrapper>
  );
};

const DscrTypesSelectionsComparison = props => {
  const {
    setDscrLineItemSelections,
    isEditing,
    isCalculating,
    lineItemType,
    selectedComparisonType1,
    selectedComparisonType2,
    comparisonCalculationFieldsInputValues1,
    comparisonCalculationFieldsInputValues2,
    calculatedComparisonValue1,
    calculatedComparisonValue2,
    calculateDscrType,
    comparisonGroupToShowLoaderIn,
    setDscrLineItemsToDefault,
    setDscrCalculationDashboardSelections
  } = props;
  const { bar1540FromFileDscrCalculation } = useFlags();

  const [shouldFocusOnOverrideInput, setShouldFocusOnOverrideInput] = useState(false);
  const [flaggedDscrTypes,setFlaggedDscrTypes] = useState(dscrTypes);

  const onSelectTypeChange = (value, selectionGroupNumber) => {
    if (selectionGroupNumber === "first") {
      setDscrLineItemSelections({ selectedComparisonType1: value, comparisonCalculationFieldsInputValues1: null });
    } else {
      setDscrLineItemSelections({ selectedComparisonType2: value, comparisonCalculationFieldsInputValues2: null });
    }
  };

  const getDscrTypeOptions = () => {
    if (!selectedComparisonType1 && !selectedComparisonType2) {
      return flaggedDscrTypes;
    }

    return _.cloneDeep(flaggedDscrTypes).filter(type => type?.type !== selectedComparisonType1?.type && type?.type !== selectedComparisonType2?.type);
  };

  const handleInputValueChange = (e, comparisonGroup) => {
    setShouldFocusOnOverrideInput(false);
    if (parseFloat(e.target?.value) < 0) {
      e.target.value = "";
    }
    const comparisonInputValues = comparisonGroup === "first" ? comparisonCalculationFieldsInputValues1 : comparisonCalculationFieldsInputValues2;
    const updatedValues =
      comparisonInputValues &&
      comparisonInputValues.map(field => (field.fieldName === e.target.name ? { ...field, inputValue: e.target.value } : { ...field }));
    const group = comparisonGroup === "first" ? "1" : "2";
    setDscrCalculationDashboardSelections({
      wasContentUpdated: true
    });
    setDscrLineItemSelections({ [`comparisonCalculationFieldsInputValues${group}`]: updatedValues, [`calculatedComparisonValue${group}`]: null });
  };

  useEffect(()=>{
    if(!bar1540FromFileDscrCalculation){
      setFlaggedDscrTypes(flaggedDscrTypes.filter(dt => dt.type !== "From file"));
    }
  },[]);

  useEffect(() => {
    if (!isEditing) {
      const inputValuesMapping =
        selectedComparisonType1 &&
        selectedComparisonType1.calculationFields &&
        selectedComparisonType1.calculationFields.map(calcField => {
          const mapping = { fieldName: calcField.fieldLabel, inputValue: "" };
          if (calcField.additionalFields) {
            mapping.additionalInputValue = null;
          }
          if (calcField.includeToggle) {
            mapping.shouldOverrideRate = false;
          }

          return mapping;
        });
      setDscrLineItemSelections({
        comparisonCalculationFieldsInputValues1: inputValuesMapping,
        calculatedComparisonValue1: selectedComparisonType1?.value || null
      });
      if (selectedComparisonType1?.type === DSCR_TYPE_NAMES.MOST_RECENT_PAYMENT) {
        calculateDscrType("first");
      }
    } else {
      setDscrLineItemSelections({ isEditing: false });
    }
  }, [selectedComparisonType1]);

  useEffect(() => {
    if (!isEditing) {
      const inputValuesMapping =
        selectedComparisonType2 &&
        selectedComparisonType2.calculationFields &&
        selectedComparisonType2.calculationFields.map(calcField => {
          const mapping = { fieldName: calcField.fieldLabel, inputValue: "" };
          if (calcField.additionalFields) {
            mapping.additionalInputValue = null;
          }

          return mapping;
        });
      setDscrLineItemSelections({
        comparisonCalculationFieldsInputValues2: inputValuesMapping,
        calculatedComparisonValue2: selectedComparisonType2?.value || null
      });
      if (selectedComparisonType2?.type === DSCR_TYPE_NAMES.MOST_RECENT_PAYMENT) {
        calculateDscrType("second");
      }
    } else {
      setDscrLineItemSelections({ isEditing: false });
    }
  }, [selectedComparisonType2]);

  useEffect(() => () => setDscrLineItemsToDefault(), []);

  const isReadyToCalculate = group => {
    const calculatedValue = group === "first" ? calculatedComparisonValue1 : calculatedComparisonValue2;
    if (calculatedValue !== null) {
      return false;
    }
    const calculations = group === "first" ? comparisonCalculationFieldsInputValues1 : comparisonCalculationFieldsInputValues2;
    const selectedComparisonType = group === "first" ? selectedComparisonType1 : selectedComparisonType2;

    return calculations && selectedComparisonType && validateDscrInputs(calculations, selectedComparisonType.type);
  };

  const fieldInputsGroup1 = useMemo(() => {
    return flaggedDscrTypes.find(type => type.type === selectedComparisonType1?.type)?.calculationFields;
  }, [selectedComparisonType1?.type]);

  const fieldInputsGroup2 = useMemo(() => {
    return flaggedDscrTypes.find(type => type.type === selectedComparisonType2?.type)?.calculationFields;
  }, [selectedComparisonType2?.type]);

  const getToggleValue = useCallback(
    (group) => {
      const groupInputValues = group === "first" ? comparisonCalculationFieldsInputValues1 : comparisonCalculationFieldsInputValues2;
      return groupInputValues && groupInputValues.find((field) => field.fieldName === "override hypothetical amortization rate?")?.shouldOverrideRate;
    },
    [comparisonCalculationFieldsInputValues1, comparisonCalculationFieldsInputValues2]
  );

  const onToggleChange = (group) => {
    const groupIndex = group === "first" ? "1" : "2";
    const inputGroupToUpdate = group === "first" ? comparisonCalculationFieldsInputValues1 : comparisonCalculationFieldsInputValues2;
    const updatedValue = inputGroupToUpdate.map((field) =>
      field.fieldName === "override hypothetical amortization rate?" ? { ...field, shouldOverrideRate: !getToggleValue(group), inputValue: "" } : { ...field }
    );
    setDscrLineItemSelections({ [`comparisonCalculationFieldsInputValues${groupIndex}`]: updatedValue, [`calculatedComparisonValue${groupIndex}`]: null });

    if (!getToggleValue(group)) {
      setShouldFocusOnOverrideInput(true);
    }
  };

  return (
    <>
      <ComparisonHeaderContainer>
        <ResultPreviewContainer>
          <PreviewWrapper left>
            <ResultPreviewLabel>
              {isCalculating && comparisonGroupToShowLoaderIn === "first" ? (
                <CircularProgress size={20} style={{ color: "#005AA9" }} />
              ) : (
                <LabelDollarFormatted value={calculatedComparisonValue1} decimals={2} />
              )}
            </ResultPreviewLabel>
            <PreviewButtonWrapper>
              <StyledButtonText
                reverse
                label="Preview"
                onClick={() => calculateDscrType("first")}
                disabled={(selectedComparisonType1 && selectedComparisonType1.type ===  DSCR_TYPE_NAMES.FROM_FILE) || !isReadyToCalculate("first")
                          || (selectedComparisonType1 && selectedComparisonType1.type === DSCR_TYPE_NAMES.MOST_RECENT_PAYMENT)}
              />
            </PreviewButtonWrapper>
          </PreviewWrapper>
          <PreviewWrapper>
            <ResultPreviewLabel>
              {isCalculating && comparisonGroupToShowLoaderIn === "second" ? (
                <CircularProgress size={20} style={{ color: "#005AA9" }} />
              ) : (
                <LabelDollarFormatted value={calculatedComparisonValue2} decimals={2} />
              )}
            </ResultPreviewLabel>
            <PreviewButtonWrapper>
              <StyledButtonText
                reverse
                label="Preview"
                onClick={() => calculateDscrType("second")}
                disabled={(selectedComparisonType2 && selectedComparisonType2.type ===  DSCR_TYPE_NAMES.FROM_FILE) || !isReadyToCalculate("second")
                           || (selectedComparisonType2&& selectedComparisonType2.type === DSCR_TYPE_NAMES.MOST_RECENT_PAYMENT)}
              />
            </PreviewButtonWrapper>
          </PreviewWrapper>
        </ResultPreviewContainer>
        <TypeSelectionContainerComparison>
          <InputDropdownWrapper>
            <InputTypeAhead
              variant="filled"
              id="type1"
              name="dscrType"
              label="Select DS Type"
              options={getDscrTypeOptions()}
              displayWith={(option) => option && option.type}
              setValueWith={(option) => option}
              onChange={(value) => onSelectTypeChange(value, "first")}
              value={selectedComparisonType1}
            />
          </InputDropdownWrapper>
          <StyledOr>OR</StyledOr>
          <InputDropdownWrapper>
            <InputTypeAhead
              variant="filled"
              id="type2"
              name="dscrType"
              label="Select DS Type"
              options={getDscrTypeOptions()}
              displayWith={(option) => option && option.type}
              setValueWith={(option) => option}
              onChange={(value) => onSelectTypeChange(value, "second")}
              value={selectedComparisonType2}
            />
          </InputDropdownWrapper>
        </TypeSelectionContainerComparison>
      </ComparisonHeaderContainer>
      <InputGroupsContainer>
        <InputGroupSection>
          {selectedComparisonType1?.type === DSCR_TYPE_NAMES.FROM_FILE && <DscrFromFile calculationComponentType="comparison1" isComparison { ...props } /> }
          {fieldInputsGroup1 &&
            fieldInputsGroup1.map((field) => {
              const group = "first";
              return (
                <>
                  {field.includeToggle && (
                    <OverrideSwitchComparison value={getToggleValue(group)} fieldLabel={field.fieldLabel} onToggleChange={() => onToggleChange(group)} />
                  )}
                  <InputGroup
                    key={field.fieldLabel}
                    field={field}
                    handleChange={(e) => handleInputValueChange(e, group)}
                    calculationFieldsInputValues={comparisonCalculationFieldsInputValues1}
                    comparisonGroup={group}
                    lineItemType={lineItemType}
                    fullWidth={lineItemType === "greaterOf"}
                    setDscrLineItemSelections={setDscrLineItemSelections}
                    shouldFocusOnOverrideInput={shouldFocusOnOverrideInput}
                  />
                </>
              );
            })}
        </InputGroupSection>
        <VerticalSeparator />
        <InputGroupSection>
          {selectedComparisonType2?.type === DSCR_TYPE_NAMES.FROM_FILE && <DscrFromFile calculationComponentType="comparison2" isComparison { ...props } /> }
          {fieldInputsGroup2 &&
            fieldInputsGroup2.map((field) => {
              const group = "second";
              return (
                <>
                  {field.includeToggle && (
                    <OverrideSwitchComparison value={getToggleValue(group)} fieldLabel={field.fieldLabel} onToggleChange={() => onToggleChange(group)} />
                  )}
                  <InputGroup
                    key={field.fieldLabel}
                    field={field}
                    handleChange={(e) => handleInputValueChange(e, group)}
                    calculationFieldsInputValues={comparisonCalculationFieldsInputValues2}
                    comparisonGroup={group}
                    lineItemType={lineItemType}
                    fullWidth={lineItemType === "greaterOf"}
                    setDscrLineItemSelections={setDscrLineItemSelections}
                    shouldFocusOnOverrideInput={shouldFocusOnOverrideInput}
                  />
                </>
              );
            })}
        </InputGroupSection>
      </InputGroupsContainer>
    </>
  );
};

export default DscrTypesSelectionsComparison;